import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Stack,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { INT32_MAX } from "@intentsify/utils";
import { Loader } from "components/Loader";
import { useConfirm } from "components/ConfirmDialog/useConfirm";
import { ChangeEvent, useState } from "react";
import { McpUsage } from "../McpUsage";
import {
  getMcpUsageSummary,
  updateMcpCredits,
} from "../McpUsage/McpUsage.requests";
import { getMcpAccess, updateMcpAccess } from "./McpAccessConfig.requests";

const McpAccessConfig = ({ companyId }: { companyId: number }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const accessKey = ["companyMcpAccess", companyId];
  const summaryKey = ["companyMcpUsageSummary", companyId];
  // Draft is undefined until the admin edits; then it overrides the server value.
  const [draft, setDraft] = useState<string>();

  const access = useQuery({
    queryKey: accessKey,
    queryFn: () => getMcpAccess(companyId),
  });

  const summary = useQuery({
    queryKey: summaryKey,
    queryFn: () => getMcpUsageSummary(companyId),
  });

  const update = useMutation({
    mutationFn: (enabled: boolean) => updateMcpAccess(companyId, enabled),
    onSuccess: (nextAccess) => {
      queryClient.setQueryData(accessKey, nextAccess);
      // Enabling creates the allocation; refresh usage so the credits field shows it.
      void queryClient.invalidateQueries(summaryKey);
      toast({ title: "MCP access updated.", status: "success" });
    },
    onError: () => {
      toast({ title: "Failed to update MCP access.", status: "error" });
    },
  });

  const updateCredits = useMutation({
    mutationFn: (totalCredits: number) =>
      updateMcpCredits(companyId, totalCredits),
    onSuccess: async () => {
      await queryClient.invalidateQueries(summaryKey);
      setDraft(undefined);
      toast({ title: "MCP credits updated.", status: "success" });
    },
    onError: () => {
      toast({ title: "Failed to update MCP credits.", status: "error" });
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    if (enabled) {
      update.mutate(true);
      return;
    }

    void confirm({
      title: "Disable MCP access?",
      description:
        "Disabling MCP access revokes all existing MCP grants for this company.",
      confirmText: "Disable MCP access",
    }).then((confirmed) => {
      if (confirmed) update.mutate(false);
    });
  };

  if (access.isLoading || !access.data) return <Loader />;

  const totalCredits = summary.data?.totalCredits ?? 0;
  const inputValue = draft ?? String(totalCredits);
  const parsed = Number(inputValue);
  const canSaveCredits =
    inputValue !== "" &&
    Number.isInteger(parsed) &&
    parsed >= 0 &&
    parsed <= INT32_MAX &&
    parsed !== totalCredits &&
    !updateCredits.isLoading;

  return (
    <Stack spacing={3}>
      <FormControl display="flex" alignItems="center">
        <FormLabel htmlFor="mcp-access-enabled" mb="0">
          Enable MCP access
        </FormLabel>
        <Switch
          id="mcp-access-enabled"
          aria-label="Enable MCP access"
          colorScheme="teal"
          isChecked={access.data.enabled}
          isDisabled={update.isLoading}
          onChange={handleChange}
        />
      </FormControl>
      <Text color="gray.500" fontSize="sm">
        Company users can connect approved MCP clients. Production access is
        read-only.
      </Text>

      {access.data.enabled && (
        <>
          <Flex align="flex-end" gap={3}>
            <FormControl maxW="240px">
              <FormLabel htmlFor="mcp-total-credits">Total Credits</FormLabel>
              <NumberInput
                min={0}
                max={INT32_MAX}
                precision={0}
                value={inputValue}
                onChange={setDraft}
              >
                <NumberInputField id="mcp-total-credits" />
              </NumberInput>
            </FormControl>
            <Button
              colorScheme="teal"
              isDisabled={!canSaveCredits}
              isLoading={updateCredits.isLoading}
              onClick={() => updateCredits.mutate(parsed)}
            >
              Save
            </Button>
          </Flex>
          <McpUsage companyId={companyId} />
        </>
      )}
    </Stack>
  );
};

export { McpAccessConfig };
